import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '../utils/api';
import './RiskMatrix.css';

function RiskMatrix({ currentUser }) {
  const isViewer = currentUser?.role === 'viewer';
  const isStaff = currentUser?.role === 'staff';
  const canEditSettings = !isViewer && !isStaff; // Only admins can edit settings
  
  const [riskSettings, setRiskSettings] = useState({
    daysThreshold: 60,
    daysPointsPerDay: 10,
    daysMaxPoints: 50,
    placementRacesCount: 10,
    placementDeclineThreshold: 2,
    placementDeclinePoints: 20,
    incidentPointsPerIncident: 10,
    ageThreshold: 4,
    agePointsPerYear: 5,
    riskLevelLow: 30,
    riskLevelMedium: 50,
    riskLevelHigh: 70,
    riskLevelCritical: 100,
    alertScoreThreshold: 70,
    newDogDaysThreshold: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState({
    minLastRaceDate: '',
    enabledTracks: [],
    incidentPatterns: []
  });
  const [newPattern, setNewPattern] = useState('');
  const [availableTracks, setAvailableTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  useEffect(() => {
    fetchSettings();
    fetchDefaultFilters();
    fetchAvailableTracks();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/risk-matrix/settings');
      setRiskSettings(response.data);
    } catch (error) {
      console.error('Error fetching risk matrix settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultFilters = async () => {
    try {
      const response = await api.get('/api/dog-filters/settings');
      console.log('Fetched default filters:', response.data);
      if (response.data) {
        setDefaultFilters({
          minLastRaceDate: response.data.minLastRaceDate || '',
          enabledTracks: Array.isArray(response.data.enabledTracks) ? response.data.enabledTracks : [],
          incidentPatterns: Array.isArray(response.data.incidentPatterns) ? response.data.incidentPatterns : []
        });
      }
    } catch (error) {
      console.error('Error fetching default filters:', error);
    }
  };

  const fetchAvailableTracks = async () => {
    try {
      setLoadingTracks(true);
      const response = await api.get('/api/dogs/filters');
      if (response.data && response.data.tracks) {
        // Tracks now come as objects with name and country
        const tracks = response.data.tracks.map(track => 
          typeof track === 'string' ? { name: track, country: null } : track
        );
        setAvailableTracks(tracks);
      }
    } catch (error) {
      console.error('Error fetching available tracks:', error);
    } finally {
      setLoadingTracks(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await api.post('/api/risk-matrix/settings', riskSettings);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving risk matrix settings:', error);
      setMessage('Failed to save settings. Please try again.');
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultFilters = async () => {
    try {
      setSaving(true);
      setMessage(null);
      console.log('Saving default filters:', defaultFilters);
      const response = await api.post('/api/dog-filters/settings', defaultFilters);
      console.log('Save response:', response.data);
      setMessage('Default filters saved successfully!');
      setTimeout(() => setMessage(null), 3000);
      // Reload filters to ensure we have the latest from server
      await fetchDefaultFilters();
    } catch (error) {
      console.error('Error saving default filters:', error);
      setMessage('Failed to save default filters. Please try again.');
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPattern = () => {
    const value = (newPattern || '').trim();
    if (!value) return;
    setDefaultFilters(prev => {
      const existing = prev.incidentPatterns || [];
      // Case-insensitive de-dup
      if (existing.some(p => p.toLowerCase() === value.toLowerCase())) return prev;
      return { ...prev, incidentPatterns: [...existing, value] };
    });
    setNewPattern('');
  };

  const handleRemovePattern = (pattern) => {
    setDefaultFilters(prev => ({
      ...prev,
      incidentPatterns: (prev.incidentPatterns || []).filter(p => p !== pattern)
    }));
  };

  const handleResetIncidentPatterns = () => {
    // Empty list saved -> backend falls back to its built-in defaults on next read.
    setDefaultFilters(prev => ({ ...prev, incidentPatterns: [] }));
  };

  const handleTrackToggle = (track) => {
    setDefaultFilters(prev => {
      const enabledTracks = prev.enabledTracks || [];
      const isEnabled = enabledTracks.includes(track);
      return {
        ...prev,
        enabledTracks: isEnabled
          ? enabledTracks.filter(t => t !== track)
          : [...enabledTracks, track]
      };
    });
  };

  const handleReset = () => {
    const defaultSettings = {
      daysThreshold: 60,
      daysPointsPerDay: 10,
      daysMaxPoints: 50,
      placementRacesCount: 10,
      placementDeclineThreshold: 2,
      placementDeclinePoints: 20,
      incidentPointsPerIncident: 10,
      ageThreshold: 4,
      agePointsPerYear: 5,
      riskLevelLow: 30,
      riskLevelMedium: 50,
      riskLevelHigh: 70,
      riskLevelCritical: 100,
      alertScoreThreshold: 70,
      newDogDaysThreshold: 7
    };
    setRiskSettings(defaultSettings);
  };

  const handleChange = (key, value) => {
    setRiskSettings(prev => ({
      ...prev,
      [key]: typeof value === 'string' ? (parseFloat(value) || 0) : value
    }));
  };

  if (loading) {
    return (
      <div className="risk-matrix">
        <div className="loading">Loading risk matrix settings...</div>
      </div>
    );
  }

  return (
    <div className="risk-matrix">
      <div className="matrix-header">
        <div>
          <h2>Settings</h2>
          <p>Configure risk matrix settings and default dog filters</p>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="risk-settings-container">
        <div className="settings-grid">
          <div className="settings-section">
            <h3>Days Since Last Race</h3>
            <p className="section-description">Configure scoring for dogs that haven't raced recently</p>
            <div className="setting-row">
              <label>Threshold (days):</label>
              <input
                type="number"
                value={riskSettings.daysThreshold}
                onChange={(e) => handleChange('daysThreshold', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Dogs not raced for more than this many days will be scored</span>
            </div>
            <div className="setting-row">
              <label>Points per day:</label>
              <input
                type="number"
                step="0.1"
                value={riskSettings.daysPointsPerDay}
                onChange={(e) => handleChange('daysPointsPerDay', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Points added for each day over the threshold</span>
            </div>
            <div className="setting-row">
              <label>Max points cap:</label>
              <input
                type="number"
                value={riskSettings.daysMaxPoints}
                onChange={(e) => handleChange('daysMaxPoints', e.target.value)}
                min="0"
                max="100"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Maximum points from this factor (0-100)</span>
            </div>
          </div>

          <div className="settings-section">
            <h3>Average Race Placement Decline</h3>
            <p className="section-description">Score based on decline in average race placement. Compares average placement over the last XX races to previous races. If placement has declined by X amount, points are triggered.</p>
            <div className="setting-row">
              <label>Number of recent races:</label>
              <input
                type="number"
                value={riskSettings.placementRacesCount}
                onChange={(e) => handleChange('placementRacesCount', e.target.value)}
                min="1"
                max="50"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Number of most recent races to compare against previous races</span>
            </div>
            <div className="setting-row">
              <label>Placement decline threshold:</label>
              <input
                type="number"
                step="0.1"
                value={riskSettings.placementDeclineThreshold}
                onChange={(e) => handleChange('placementDeclineThreshold', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">If average placement has worsened by this amount, trigger points</span>
            </div>
            <div className="setting-row">
              <label>Points for decline:</label>
              <input
                type="number"
                value={riskSettings.placementDeclinePoints}
                onChange={(e) => handleChange('placementDeclinePoints', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Points added when placement decline threshold is met</span>
            </div>
          </div>

          <div className="settings-section">
            <h3>Age</h3>
            <p className="section-description">Score based on age. Only applies if the dog hasn't been seen for over 30 days. Each dog receives 10 points for each year over 4.</p>
            <div className="setting-row">
              <label>Age threshold (years):</label>
              <input
                type="number"
                value={riskSettings.ageThreshold}
                onChange={(e) => handleChange('ageThreshold', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Age above this triggers scoring (default: 4)</span>
            </div>
            <div className="setting-row">
              <label>Points per year over threshold:</label>
              <input
                type="number"
                value={riskSettings.agePointsPerYear}
                onChange={(e) => handleChange('agePointsPerYear', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Points added for each year over threshold (default: 10, only if not seen 30+ days)</span>
            </div>
          </div>

          <div className="settings-section">
            <h3>Incidents</h3>
            <p className="section-description">Score based on collisions or disqualifications in the most recent race. Only applies if the dog hasn't been seen for over 30 days. If there's a collision or disqualification in their most recent race, the dog receives 30 points.</p>
            <div className="setting-row">
              <label>Points for most recent race incident:</label>
              <input
                type="number"
                value={riskSettings.incidentPointsPerIncident}
                onChange={(e) => handleChange('incidentPointsPerIncident', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Points added for collision/disqualification in most recent race (default: 30, only if not seen 30+ days)</span>
            </div>
          </div>

          <div className="settings-section">
            <h3>Risk Level Thresholds</h3>
            <p className="section-description">Define score ranges for each risk level</p>
            <div className="setting-row">
              <label>Low (0 - X):</label>
              <input
                type="number"
                value={riskSettings.riskLevelLow}
                onChange={(e) => handleChange('riskLevelLow', e.target.value)}
                min="0"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Scores 0 to this value are considered Low risk</span>
            </div>
            <div className="setting-row">
              <label>Medium (X+1 - Y):</label>
              <input
                type="number"
                value={riskSettings.riskLevelMedium}
                onChange={(e) => handleChange('riskLevelMedium', e.target.value)}
                min={riskSettings.riskLevelLow + 1}
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Scores between Low and this value are Medium risk</span>
            </div>
            <div className="setting-row">
              <label>High (Y+1 - Z):</label>
              <input
                type="number"
                value={riskSettings.riskLevelHigh}
                onChange={(e) => handleChange('riskLevelHigh', e.target.value)}
                min={riskSettings.riskLevelMedium + 1}
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Scores between Medium and this value are High risk</span>
            </div>
            <div className="setting-row">
              <label>Critical (Z+1 - 100):</label>
              <span className="readonly-value">Auto (scores above High threshold)</span>
              <span className="setting-hint">Scores above High threshold are Critical risk</span>
            </div>
          </div>

          <div className="settings-section">
            <h3>Alert Settings</h3>
            <p className="section-description">Configure thresholds for generating alerts in daily reports</p>
            <div className="setting-row">
              <label>Score alert threshold:</label>
              <input
                type="number"
                value={riskSettings.alertScoreThreshold}
                onChange={(e) => handleChange('alertScoreThreshold', e.target.value)}
                min="0"
                max="100"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Alert when a dog's risk score exceeds this value</span>
            </div>
            <div className="setting-row">
              <label>New dog days threshold:</label>
              <input
                type="number"
                value={riskSettings.newDogDaysThreshold}
                onChange={(e) => handleChange('newDogDaysThreshold', e.target.value)}
                min="1"
                disabled={!canEditSettings}
                readOnly={!canEditSettings}
              />
              <span className="setting-hint">Alert when a dog's first race has been within this many days</span>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="settings-section" style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Default Dog Filters Applied to All</h3>
            <p className="section-description" style={{ marginBottom: '16px' }}>
              Configure default filters that will be automatically applied to the Dog Management page
            </p>
            
            <div className="setting-row" style={{ marginBottom: '16px' }}>
              <label style={{ minWidth: '200px' }}>Minimum Last Race Date:</label>
              <input
                type="date"
                value={defaultFilters.minLastRaceDate}
                onChange={(e) => setDefaultFilters(prev => ({ ...prev, minLastRaceDate: e.target.value }))}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <span className="setting-hint" style={{ marginLeft: '12px' }}>
                Dogs that haven't raced after this date will be filtered out
              </span>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Enabled Tracks ({defaultFilters.enabledTracks?.length || 0} selected):
              </label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '1px solid var(--border)', 
                borderRadius: '4px', 
                padding: '8px',
                backgroundColor: 'var(--bg)'
              }}>
                {loadingTracks ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Loading tracks...
                  </div>
                ) : availableTracks.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    No tracks available
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                    {availableTracks.map(track => {
                      const trackName = typeof track === 'string' ? track : track.name;
                      const trackCountry = typeof track === 'object' ? track.country : null;
                      const isEnabled = defaultFilters.enabledTracks?.includes(trackName) || false;
                      return (
                        <label
                          key={trackName}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            backgroundColor: isEnabled ? 'var(--bg-secondary)' : 'transparent',
                            border: `1px solid ${isEnabled ? 'var(--primary)' : 'var(--border)'}`
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handleTrackToggle(trackName)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', flex: 1 }}>{trackName}</span>
                          {trackCountry && (
                            <img 
                              src={trackCountry === 'GB' ? '/gb.webp' : '/roi.webp'}
                              alt={trackCountry === 'GB' ? 'United Kingdom' : 'Republic of Ireland'}
                              title={trackCountry === 'GB' ? 'United Kingdom' : 'Republic of Ireland'}
                              style={{ width: '20px', height: '10px', objectFit: 'contain' }}
                            />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <span className="setting-hint" style={{ marginTop: '8px', display: 'block' }}>
                Only dogs that have raced at enabled tracks will be shown on the Dog Management page
              </span>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Incident Keywords ({defaultFilters.incidentPatterns?.length || 0}):
              </label>
              <div style={{
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: 'var(--bg)',
                marginBottom: '8px'
              }}>
                {(defaultFilters.incidentPatterns || []).length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    No keywords saved — built-in defaults will be used.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(defaultFilters.incidentPatterns || []).map(pattern => (
                      <span
                        key={pattern}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '999px',
                          fontSize: '13px',
                          fontFamily: 'monospace'
                        }}
                      >
                        {pattern}
                        <button
                          type="button"
                          onClick={() => handleRemovePattern(pattern)}
                          aria-label={`Remove ${pattern}`}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '14px',
                            lineHeight: 1,
                            padding: 0
                          }}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPattern(); } }}
                  placeholder="Add keyword (e.g. Fell, KO, DNF)..."
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddPattern}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >Add</button>
                <button
                  type="button"
                  onClick={handleResetIncidentPatterns}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >Reset to defaults</button>
              </div>
              <span className="setting-hint" style={{ marginTop: '8px', display: 'block' }}>
                Race result comments and finish positions are scanned for these keywords (case-insensitive). Short keywords (4 chars or fewer) match whole words only — e.g. <code>KO</code> matches "KO" but not "joke". Longer keywords substring-match — e.g. <code>Disqualif</code> catches "Disqualified" and "Disqualification".
              </span>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => {
                  setDefaultFilters({
                    minLastRaceDate: '',
                    enabledTracks: [],
                    incidentPatterns: []
                  });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                disabled={saving}
              >
                Clear Filters
              </button>
              <button
                onClick={handleSaveDefaultFilters}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Default Filters'}
              </button>
            </div>
          </div>
        )}

        {canEditSettings && (
          <div className="settings-actions">
            <button 
              className="reset-button"
              onClick={handleReset}
              disabled={saving}
            >
              Reset to Defaults
            </button>
            <button 
              className="save-button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
        {!canEditSettings && (
          <div style={{ padding: '16px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', marginTop: '20px' }}>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              <strong>Read-only mode:</strong> Only administrators can modify risk matrix settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RiskMatrix;

